import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, filter, map, take } from 'rxjs';
import { AuthService } from '@core/auth/auth.service';
import { BranchService } from '@core/services/branch.service';
import { OrganisationService } from '@core/services/organisation.service';
import { OrganisationListItem } from '@core/models/organisation/organisation-list-item.model';

// "Skip for now" on the signup wizard. Deliberately cleared on every login (see AuthService), so a
// user who still has no organisation is walked through it again next time.
export const ONBOARDING_SKIP_FLAG = 'onboarding_skipped_sl';

// The welcome overlay invited members get (see OnboardingWelcomeComponent). Cleared on every login
// (like the skip flag above): the overlay is re-evaluated from the user's previous login, so this flag
// only keeps it dismissed for the rest of the current session.
export const ONBOARDING_WELCOME_FLAG = 'onboarding_welcome_seen_sl';

@Injectable({ providedIn: 'root' })
export class OnboardingGuard {
  constructor(
    private authService: AuthService,
    private branchService: BranchService,
    private organisationService: OrganisationService,
    private router: Router,
  ) {}

  canActivate(): Observable<boolean | UrlTree> | boolean {
    if (!this.authService.IsLoggedIn) return true;
    if (this.branchService.vanityMode) return true;
    // A masterpass token: signing someone else's account up for an organisation is not what the
    // Smart Lions employee behind it came to do.
    if (this.authService.getToken().isImpersonating) return true;
    if (localStorage.getItem(ONBOARDING_SKIP_FLAG) === '1') return true;

    if (this.organisationService.currentOrganisationList$.value === null) {
      this.organisationService.getAll();
    }

    return this.organisationService.currentOrganisationList$.pipe(
      filter((list): list is OrganisationListItem[] => list !== null),
      take(1),
      map(list => (list.length === 0 ? this.router.parseUrl('/onboarding') : true)),
    );
  }
}
